import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { SELECT_STYLES, mergeSelectStyles } from "../../utils/selectStyles";
import { CButton, CCollapse, CFormInput, CFormSelect } from "@coreui/react";
import { Tooltip } from "react-tooltip";
import * as Sentry from "@sentry/react";
import DashboardContent from "../../components/DashboardContent";
import RedirectHandler from "../../components/RedirectHandler";
import useCsr from "../../hooks/useCsr";
import { CsrAPI } from "../../apis/CsrAPI";
import QuestionItemInfo from "../../components/QuestionItemInfo";
import { ReactComponent as BinIcon } from "../../assets/images/icons/bin.svg";
import { ReactComponent as PlusIcon } from "../../assets/images/icons/plus.svg";
import { ReactComponent as CopyIcon } from "../../assets/images/icons/copy-white.svg";
import { ReactComponent as CopyOutlineIcon } from "../../assets/images/icons/copy.svg";
import {
  SCS_COMPARISON_TYPE_OPTIONS,
  SCS_GROUPING_TABLE_OPTIONS,
  SCS_TREATMENT_GROUP_ROLE_OPTIONS,
} from "../../utils/scsDescriptionOfStudiesOptions";
import {
  SCS_OEE_INTRO_TEXT,
  SCS_OEE_OTHER_STUDIES_OPTIONS,
  SCS_OEE_QUESTIONS,
} from "../../utils/scsOverallExtentOfExposureQuestions";
import { useScsTableOptions } from "../../hooks/useScsTableOptions";
import { useScsDescriptionOfStudies } from "../../hooks/useScsDescriptionOfStudies";

const isProduction = process.env.NODE_ENV === "production";

const FORM_ID = "scs-overall-extent-of-exposure-form";
const REMOVE_TOOLTIP_ID = "scs-oee-remove";
const DUPLICATE_TOOLTIP_ID = "scs-oee-duplicate";

// Repeatable grouping blocks always keep at least one entry; the first entry is
// non-removable (mirrors the Description of Studies / CSR block pattern).
const MIN_BLOCK_ENTRIES = 1;

const emptyTreatmentGroup = () => ({ name: "", role: "" });
const emptyComparisonPair = () => ({ treatment: "", comparator: "" });

// UI shape of one grouping block. Like the Description of Studies comparison
// blocks it holds every branch answer, so switching the comparison type (Q3)
// never loses what was already typed; only the answers belonging to the
// selected type are persisted (see buildPayload).
const emptyGrouping = (name = "") => ({
  qGroupingName: name,
  // Q1 answer: null until the user picks Yes / No.
  qExposureOtherStudies: null,
  qExposureTable: "",
  qExposureComparisonType: "",
  qNonComparativeGroupNames: [""],
  qExposureTreatmentGroups: [emptyTreatmentGroup()],
  qExposureComparisonPairs: [emptyComparisonPair()],
  qExposureDoseGroups: [""],
});

// Comparison type (Q3) -> the saved section the grouping belongs to (mirrors
// COMPARISON_TYPE_SECTION on the Description of Studies page).
const COMPARISON_TYPE_SECTION = {
  "Non-Comparative": "single_arm",
  Comparative: "multi_arm",
  "Dose-Finding": "dose_finding",
};

const seedList = list =>
  Array.isArray(list) && list.length > 0 ? [...list] : [""];

// Treatment group roles are saved lowercase ("treatment" / "comparator") but
// shown with the wording of SCS_TREATMENT_GROUP_ROLE_OPTIONS on screen — same
// convention as the Description of Studies page.
const roleForPayload = role => (role || "").toLowerCase();
const roleForDisplay = role =>
  SCS_TREATMENT_GROUP_ROLE_OPTIONS.find(
    option => option.toLowerCase() === (role || "").toLowerCase()
  ) || "";

// Q1 was saved as a boolean, but tolerate the "Yes"/"No" strings an earlier
// payload may hold.
const asYesNo = value => {
  if (typeof value === "boolean") return value;
  if (value === "Yes") return true;
  if (value === "No") return false;
  return null;
};

// Deep-clone a grouping block so a duplicated block is fully independent from
// its source (mutating one never affects the other). Also used to rebuild a
// block from saved data, where only the fields of the selected comparison type
// are present.
const cloneGrouping = (group, name) => ({
  ...emptyGrouping(name ?? group?.qGroupingName ?? ""),
  qExposureOtherStudies: asYesNo(group?.qExposureOtherStudies),
  qExposureTable: group?.qExposureTable || "",
  qExposureComparisonType: group?.qExposureComparisonType || "",
  qNonComparativeGroupNames: seedList(group?.qNonComparativeGroupNames),
  qExposureTreatmentGroups: (group?.qExposureTreatmentGroups?.length
    ? group.qExposureTreatmentGroups
    : [emptyTreatmentGroup()]
  ).map(tg => ({ name: tg?.name || "", role: roleForDisplay(tg?.role) })),
  qExposureComparisonPairs: (group?.qExposureComparisonPairs?.length
    ? group.qExposureComparisonPairs
    : [emptyComparisonPair()]
  ).map(pair => ({
    treatment: pair?.treatment || "",
    comparator: pair?.comparator || "",
  })),
  qExposureDoseGroups: seedList(group?.qExposureDoseGroups),
});

// --- Saved <-> UI shape ---------------------------------------------------
//
// The reported groupings are saved into one of three sections, decided by Q3.
// Both single_arm and multi_arm store their group names under
// qExposureTreatmentGroups as { name, role } — single_arm has no role question,
// so its role is always an empty string:
//   Non-Comparative -> single_arm   { qExposureTable, qExposureTreatmentGroups }
//   Comparative     -> multi_arm    { qExposureTable, qExposureTreatmentGroups,
//                                     qExposureComparisonPairs }
//   Dose-Finding    -> dose_finding { qExposureTable, qExposureDoseGroups }
// The section carries a single top-level qExposureOtherStudies flag, and the
// entries carry no grouping name, so a grouping that answered "No" contributes
// no entry at all — see buildPayload / restoreGroupings.
const restoreGroupings = (groupings = {}) => {
  const fromSection = (entries, comparisonType, mapEntry = entry => entry) =>
    (Array.isArray(entries) ? entries : []).map(entry => {
      const type =
        entry && "qExposureComparisonType" in entry
          ? entry.qExposureComparisonType
          : comparisonType(entry);
      return cloneGrouping({
        ...mapEntry(entry, type),
        // An entry only exists for a grouping that answered "Yes" to Q1.
        qExposureOtherStudies: true,
        qExposureComparisonType: type,
      });
    });

  return [
    ...fromSection(
      groupings.single_arm,
      // A restored single_arm entry is Non-Comparative when it carries the
      // group names; otherwise Q3 was never answered.
      entry =>
        Array.isArray(entry?.qExposureTreatmentGroups) ||
        Array.isArray(entry?.qNonComparativeGroupNames)
          ? "Non-Comparative"
          : "",
      // single_arm group names are saved under qExposureTreatmentGroups but
      // belong to the Non-Comparative question (plain strings) on screen.
      (entry, type) =>
        type === "Non-Comparative"
          ? {
              ...entry,
              qExposureTreatmentGroups: undefined,
              qNonComparativeGroupNames:
                entry?.qNonComparativeGroupNames ||
                (entry?.qExposureTreatmentGroups || []).map(
                  tg => tg?.name || ""
                ),
            }
          : entry
    ),
    ...fromSection(groupings.multi_arm, () => "Comparative"),
    ...fromSection(groupings.dose_finding, () => "Dose-Finding"),
  ];
};

// The saved answers arrive either bare or wrapped in the section name.
const savedSectionFrom = payload => {
  const section = payload?.overall_extent_of_exposure || payload;
  return section && typeof section === "object" ? section : null;
};

// One block per grouping named in Q2A of the Description of Studies page,
// pre-filled from the saved answers. The saved entries carry no grouping name,
// so they are paired to the groupings by position (as the Description of
// Studies comparison blocks are). Entries left over — the extra groupings added
// with "Add another group" — keep their place at the end. Groupings with no
// entry answered "No", which is only knowable once something has been saved.
const buildGroupings = (names, saved) => {
  const savedBlocks = restoreGroupings(saved?.groupings);
  const unreported = saved ? false : null;

  const blocks = names.map((name, index) =>
    savedBlocks[index]
      ? { ...savedBlocks[index], qGroupingName: name }
      : { ...emptyGrouping(name), qExposureOtherStudies: unreported }
  );
  blocks.push(...savedBlocks.slice(names.length));

  return blocks.length > 0 ? blocks : [emptyGrouping()];
};

const toOptions = values => (values || []).map(v => ({ value: v, label: v }));

// Render a single-select's chosen value as a chip/tag so it matches the styling
// used by the Description of Studies / Subject Disposition dropdowns.
const SINGLE_VALUE_CHIP_STYLES = mergeSelectStyles(SELECT_STYLES, {
  singleValue: provided => ({
    ...provided,
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "calc(100% - 8px)",
    backgroundColor: "#eef1f5",
    borderRadius: "4px",
    padding: "3px 6px",
    margin: "2px",
    fontSize: "13px",
    boxSizing: "border-box",
  }),
});

const SCSOverallExtentOfExposure = () => {
  const { studyId: summaryId } = useParams();
  const { setAjaxLoading, wrongEnvironment } = useCsr();
  const navigate = useNavigate();

  // One accordion per grouping named in Q2A of the Description of Studies page,
  // read from the `scsData` store branch (fetched by the hook when this page is
  // landed on directly).
  const {
    groupingNames,
    isLoadingData: isLoadingGroupings,
    fetched: groupingsFetched,
  } = useScsDescriptionOfStudies(summaryId);

  // The Q2 table dropdown is populated from the safety-summary TFL content in
  // the scsData store once the uploaded files have been PROCESSED; until then
  // fall back to the bundled placeholder options.
  const { tableNames: scsTableNames } = useScsTableOptions();
  const tableOptions = scsTableNames.length
    ? scsTableNames
    : SCS_GROUPING_TABLE_OPTIONS;

  const [questionText, setQuestionText] = useState({});
  const [groupings, setGroupings] = useState([]);
  // The saved section, held until the grouping names are in so both can be
  // merged in one go (they arrive independently).
  const [savedSection, setSavedSection] = useState(null);
  const [savedLoaded, setSavedLoaded] = useState(false);
  // Which grouping accordion items are open (by array index). The first is
  // expanded when the page loads; a newly added / duplicated block (always the
  // last) becomes the only expanded one.
  const [openGroupIndexes, setOpenGroupIndexes] = useState(() => new Set([0]));

  const [groupErrors, setGroupErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRedirect, setShowRedirect] = useState(false);

  // Accordion header buttons, so answering "No" can move focus to the next
  // grouping.
  const accordionButtonRefs = useRef({});
  const pendingFocusRef = useRef(null);
  const lastGroupRef = useRef(null);
  const shouldScrollGroupRef = useRef(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Question wording is served from the shared safety-summary questions
  // endpoint; fall back to the bundled constants when it is unavailable.
  useEffect(() => {
    CsrAPI.getSafetySummaryQuestions()
      .then(data => {
        if (mountedRef.current && data) setQuestionText(data);
      })
      .catch(error => {
        if (isProduction) Sentry.captureException(error);
      });
  }, []);

  // Pre-populate previously saved wizard state.
  useEffect(() => {
    if (!summaryId) return;

    setAjaxLoading(true);
    CsrAPI.getSafetySummaryOverallExtentOfExposure({ summaryId })
      .then(data => {
        if (!mountedRef.current) return;
        setSavedSection(savedSectionFrom(data));
      })
      .catch(error => {
        if (!mountedRef.current) return;
        // A 404 simply means nothing has been saved yet — start with defaults.
        if (error?.response?.status === 404) return;
        if (isProduction) Sentry.captureException(error);
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to load previously saved data.");
        }
      })
      .finally(() => {
        if (!mountedRef.current) return;
        setSavedLoaded(true);
        setAjaxLoading(false);
      });
  }, [summaryId, setAjaxLoading]);

  // Rebuild the blocks whenever the grouping names change (they load after the
  // first render, and can be edited on the Description of Studies page between
  // visits) or the saved answers land. Only the first grouping is expanded.
  // `groupingNames` is memoized by the hook, so this doesn't fire on every
  // render and can't discard what the user has typed.
  useEffect(() => {
    if (!groupingsFetched || !savedLoaded) return;
    setGroupings(buildGroupings(groupingNames, savedSection));
    setOpenGroupIndexes(new Set([0]));
  }, [groupingNames, groupingsFetched, savedLoaded, savedSection]);

  // Scroll the newly added / duplicated grouping into view. Walk up to the
  // named scroll container so we never scroll the browser viewport (matches the
  // Description of Studies block behaviour).
  useEffect(() => {
    if (!shouldScrollGroupRef.current || !lastGroupRef.current) return;
    const el = lastGroupRef.current;
    let wrapper = el.parentElement;
    while (
      wrapper &&
      !wrapper.classList.contains("dashboard-content__wrapper")
    ) {
      wrapper = wrapper.parentElement;
    }
    if (wrapper) {
      const elTop = el.getBoundingClientRect().top;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      wrapper.scrollBy({ top: elTop - wrapperTop, behavior: "smooth" });
    }
    shouldScrollGroupRef.current = false;
  }, [groupings.length]);

  // Answering "No" hands over to the next grouping, so focus follows it there
  // once the collapse/expand has rendered.
  useEffect(() => {
    const target = pendingFocusRef.current;
    if (target === null || target === undefined) return;
    pendingFocusRef.current = null;
    accordionButtonRefs.current[target]?.focus();
  });

  // --- Grouping blocks ----------------------------------------------------
  // "Add another group" appends an empty block; "Duplicate group above"
  // appends a copy of the last block; the per-block "Duplicate" pill appends a
  // copy of that block. New blocks always go to the end and become the only
  // expanded accordion item (mirrors the Description of Studies page).
  const addGrouping = () =>
    setGroupings(prev => {
      const next = [...prev, emptyGrouping()];
      shouldScrollGroupRef.current = true;
      setOpenGroupIndexes(new Set([next.length - 1]));
      return next;
    });
  const duplicateGrouping = index =>
    setGroupings(prev => {
      if (!prev[index]) return prev;
      const next = [...prev, cloneGrouping(prev[index])];
      shouldScrollGroupRef.current = true;
      setOpenGroupIndexes(new Set([next.length - 1]));
      return next;
    });
  const duplicateGroupingAbove = () => {
    if (!groupings.length) return;
    duplicateGrouping(groupings.length - 1);
  };
  const removeGrouping = index =>
    setGroupings(prev => {
      if (prev.length <= MIN_BLOCK_ENTRIES) return prev;
      const next = prev.filter((_, i) => i !== index);
      // Leave the last remaining grouping expanded.
      setOpenGroupIndexes(new Set([next.length - 1]));
      return next;
    });
  const toggleGroup = index =>
    setOpenGroupIndexes(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });

  const updateGrouping = (index, updater) =>
    setGroupings(prev =>
      prev.map((row, i) => (i === index ? updater(row) : row))
    );

  // Q1 — "No" ends this grouping's section: collapse it and hand over to the
  // next grouping (expanded and focused) when there is one.
  const answerOtherStudies = (index, value) => {
    updateGrouping(index, row => ({ ...row, qExposureOtherStudies: value }));
    setGroupErrors(prev => {
      if (!prev[index]) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
    if (value !== false) return;

    const nextIndex = index + 1;
    const hasNext = nextIndex < groupings.length;
    setOpenGroupIndexes(prev => {
      const next = new Set(prev);
      next.delete(index);
      if (hasNext) next.add(nextIndex);
      return next;
    });
    if (hasNext) pendingFocusRef.current = nextIndex;
  };

  // Repeatable lists always keep at least one entry; the first is non-removable.
  const baseGroupNames = row =>
    (row.qNonComparativeGroupNames || []).length > 0
      ? row.qNonComparativeGroupNames
      : [""];
  const baseTreatmentGroups = row =>
    (row.qExposureTreatmentGroups || []).length > 0
      ? row.qExposureTreatmentGroups
      : [emptyTreatmentGroup()];
  const basePairs = row =>
    (row.qExposureComparisonPairs || []).length > 0
      ? row.qExposureComparisonPairs
      : [emptyComparisonPair()];
  const baseDoseGroups = row =>
    (row.qExposureDoseGroups || []).length > 0 ? row.qExposureDoseGroups : [""];

  const validate = () => {
    const errors = {};
    groupings.forEach((row, index) => {
      if (row.qExposureOtherStudies === null) {
        errors[index] = { qExposureOtherStudies: "Please select an answer" };
      }
    });
    setGroupErrors(errors);

    const firstInvalid = Object.keys(errors)[0];
    if (firstInvalid !== undefined) {
      const index = Number(firstInvalid);
      // Expand the offending grouping before focusing it, otherwise the
      // question is hidden inside a collapsed accordion item.
      setOpenGroupIndexes(prev => new Set(prev).add(index));
      pendingFocusRef.current = index;
      return false;
    }
    return true;
  };

  // Split the reported groupings into the three saved sections, decided by Q3.
  // A grouping that answered "No" to Q1 contributes no entry at all, and the
  // section's single qExposureOtherStudies flag says whether any of them did.
  const buildPayload = () => {
    const reported = groupings.filter(
      row => row.qExposureOtherStudies === true
    );
    const sections = { single_arm: [], multi_arm: [], dose_finding: [] };

    reported.forEach(row => {
      const common = { qExposureTable: row.qExposureTable || null };
      // Only the answers belonging to the comparison type picked in Q3 are
      // persisted (mirrors buildComparisons on the Description of Studies page).
      const section = COMPARISON_TYPE_SECTION[row.qExposureComparisonType];
      if (section === "multi_arm") {
        sections.multi_arm.push({
          ...common,
          qExposureTreatmentGroups: baseTreatmentGroups(row)
            .filter(tg => (tg?.name || "").trim())
            .map(tg => ({ name: tg.name, role: roleForPayload(tg.role) })),
          qExposureComparisonPairs: basePairs(row).filter(
            pair => pair?.treatment || pair?.comparator
          ),
        });
      } else if (section === "dose_finding") {
        sections.dose_finding.push({
          ...common,
          qExposureDoseGroups: baseDoseGroups(row).filter(dose =>
            (dose || "").trim()
          ),
        });
      } else if (section === "single_arm") {
        // Non-Comparative group names have no role question, so they are saved
        // under qExposureTreatmentGroups with an empty role.
        sections.single_arm.push({
          ...common,
          qExposureTreatmentGroups: baseGroupNames(row)
            .filter(name => (name || "").trim())
            .map(name => ({ name, role: "" })),
        });
      } else {
        // Q3 not answered yet — keep the table it belongs to without any branch
        // answers, so nothing is lost on reload.
        sections.single_arm.push(common);
      }
    });

    return {
      qExposureOtherStudies: reported.length > 0,
      groupings: sections,
    };
  };

  // Persists the wizard state via PATCH .../wizards/overall_extent_of_exposure.
  // Shared by the "Save" button (stays on the page) and the "Analysis of
  // Adverse Events" button (navigates on success); returns whether the save
  // succeeded so the caller can decide what to do next.
  const saveWizardState = async () => {
    if (isSubmitting) return false;
    if (!validate()) {
      toast.error("Please fill in all required fields.");
      return false;
    }

    setIsSubmitting(true);
    try {
      await CsrAPI.saveSafetySummaryOverallExtentOfExposure({
        summaryId,
        state: buildPayload(),
      });
      return true;
    } catch (error) {
      if (isProduction) Sentry.captureException(error);
      if (mountedRef.current) {
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to save. Please try again.");
        }
      }
      return false;
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      navigate(`/scs/${summaryId}/analysis-of-adverse-events`);
    }
  };

  const handleSave = async event => {
    event?.preventDefault();
    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      toast.success("Data saved successfully!");
    }
  };

  const stringSelectOptions = values => [
    { value: "", label: "Select..." },
    ...values.map(v => ({ value: v, label: v })),
  ];

  const removeButton = (label, onClick) => (
    <CButton
      color={"danger"}
      shape={"pill"}
      aria-label={label}
      data-tooltip-id={REMOVE_TOOLTIP_ID}
      data-tooltip-content={label}
      onClick={onClick}>
      <BinIcon />
    </CButton>
  );

  const addButton = (label, onClick) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      onClick={onClick}>
      {label} <PlusIcon />
    </CButton>
  );

  // Solid-blue pill sitting at the top of each grouping block.
  const duplicateBlockButton = onClick => (
    <CButton
      color={"primary"}
      shape={"pill"}
      className={"scs-dos__duplicate"}
      data-tooltip-id={DUPLICATE_TOOLTIP_ID}
      data-tooltip-content={"Duplicate this grouping"}
      onClick={onClick}>
      <CopyIcon /> Duplicate
    </CButton>
  );

  // Outlined pill pairing with "Add another group" at the bottom of the page.
  const duplicateAboveButton = (label, onClick) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      onClick={onClick}>
      {label} <CopyOutlineIcon />
    </CButton>
  );

  // Endpoint wording wins; fall back to the bundled question text.
  const qText = key => {
    const question = SCS_OEE_QUESTIONS[key];
    return (
      questionText[question?.textKey || key] ||
      questionText[key] ||
      question?.text ||
      ""
    );
  };

  // CSR-style numbered question: index badge in the left gutter, label +
  // control in the item column (mirrors QuestionItem).
  const renderQuestion = (key, control, numberOverride) => {
    const question = SCS_OEE_QUESTIONS[key];
    if (!question) return null;
    return (
      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>
            {numberOverride ?? question.number}
          </span>
        </div>
        <div className={"questions-content__item"}>
          <div className={"questions-content__label"}>
            <span className={"questions-content__label-text"}>
              {qText(key)}
              {question.required && (
                <span className={"questions-content__required-asterisk"}>
                  {" "}
                  *
                </span>
              )}
            </span>
            {question.tooltip && (
              <QuestionItemInfo
                questionName={key}
                notes={{ [key]: question.tooltip }}
              />
            )}
          </div>
          <div className={"questions-content__input"}>{control}</div>
        </div>
      </div>
    );
  };

  // Q1 — Yes / No toggle. "No" ends this grouping's section.
  const renderOtherStudies = (group, index, numberOverride) => {
    const error = groupErrors[index]?.qExposureOtherStudies;
    return renderQuestion(
      "qExposureOtherStudies",
      <>
        {SCS_OEE_OTHER_STUDIES_OPTIONS.map(option => (
          <React.Fragment key={String(option.value)}>
            <input
              type={"radio"}
              className={"btn-check"}
              name={`exposure-other-studies-${index}`}
              id={`exposure-other-studies-${index}-${option.label}`}
              autoComplete={"off"}
              checked={group.qExposureOtherStudies === option.value}
              onChange={() => answerOtherStudies(index, option.value)}
            />
            <label
              className={"btn btn-light"}
              htmlFor={`exposure-other-studies-${index}-${option.label}`}>
              {option.label}
            </label>
          </React.Fragment>
        ))}
        {error && <span className={"errmsg"}>{error}</span>}
      </>,
      numberOverride
    );
  };

  // Q2 — single-select of the SCS TFL tables.
  const renderTable = (group, index, numberOverride) =>
    renderQuestion(
      "qExposureTable",
      <Select
        className={"scs-dos__control"}
        styles={SINGLE_VALUE_CHIP_STYLES}
        options={toOptions(tableOptions)}
        value={
          group.qExposureTable
            ? { value: group.qExposureTable, label: group.qExposureTable }
            : null
        }
        onChange={selected =>
          updateGrouping(index, row => ({
            ...row,
            qExposureTable: selected ? selected.value : "",
          }))
        }
        placeholder={"Select table"}
        isClearable
      />,
      numberOverride
    );

  // Q3 — comparison type, the same single-select button group as Q7 of the
  // Description of Studies page.
  const renderComparisonType = (group, index, numberOverride) =>
    renderQuestion(
      "qExposureComparisonType",
      SCS_COMPARISON_TYPE_OPTIONS.map(option => (
        <React.Fragment key={option}>
          <input
            type={"radio"}
            className={"btn-check"}
            name={`comparison-type-${index}`}
            id={`comparison-type-${index}-${option}`}
            autoComplete={"off"}
            checked={(group.qExposureComparisonType || "") === option}
            onChange={() =>
              updateGrouping(index, row => ({
                ...row,
                qExposureComparisonType: option,
              }))
            }
          />
          <label
            className={"btn btn-light"}
            htmlFor={`comparison-type-${index}-${option}`}>
            {option}
          </label>
        </React.Fragment>
      )),
      numberOverride
    );

  const renderNonComparativeGroupNames = (group, index, numberOverride) =>
    renderQuestion(
      "qNonComparativeGroupNames",
      <>
        {baseGroupNames(group).map((groupName, gnIndex) => (
          <div
            key={`ncgn-${index}-${gnIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <CFormInput
                value={groupName}
                placeholder={"Population label"}
                onChange={e =>
                  updateGrouping(index, row => ({
                    ...row,
                    qNonComparativeGroupNames: baseGroupNames(row).map(
                      (g, i) => (i === gnIndex ? e.target.value : g)
                    ),
                  }))
                }
              />
            </div>
            {baseGroupNames(group).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove group", () =>
                updateGrouping(index, row => ({
                  ...row,
                  qNonComparativeGroupNames: baseGroupNames(row).filter(
                    (_, i) => i !== gnIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add another", () =>
          updateGrouping(index, row => ({
            ...row,
            qNonComparativeGroupNames: [...baseGroupNames(row), ""],
          }))
        )}
      </>,
      numberOverride
    );

  const renderTreatmentGroups = (group, index, numberOverride) =>
    renderQuestion(
      "qExposureTreatmentGroups",
      <>
        {baseTreatmentGroups(group).map((tg, tgIndex) => (
          <div
            key={`tg-${index}-${tgIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <label>Name</label>
              <CFormInput
                value={tg.name}
                placeholder={"Treatment group name"}
                onChange={e =>
                  updateGrouping(index, row => ({
                    ...row,
                    qExposureTreatmentGroups: baseTreatmentGroups(row).map(
                      (t, i) =>
                        i === tgIndex ? { ...t, name: e.target.value } : t
                    ),
                  }))
                }
              />
            </div>
            <div className={"form-component"}>
              <label>Role</label>
              <CFormSelect
                value={tg.role}
                options={stringSelectOptions(SCS_TREATMENT_GROUP_ROLE_OPTIONS)}
                onChange={e =>
                  updateGrouping(index, row => ({
                    ...row,
                    qExposureTreatmentGroups: baseTreatmentGroups(row).map(
                      (t, i) =>
                        i === tgIndex ? { ...t, role: e.target.value } : t
                    ),
                  }))
                }
              />
            </div>
            {baseTreatmentGroups(group).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove treatment group", () =>
                updateGrouping(index, row => ({
                  ...row,
                  qExposureTreatmentGroups: baseTreatmentGroups(row).filter(
                    (_, i) => i !== tgIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Treatment Group", () =>
          updateGrouping(index, row => ({
            ...row,
            qExposureTreatmentGroups: [
              ...baseTreatmentGroups(row),
              emptyTreatmentGroup(),
            ],
          }))
        )}
      </>,
      numberOverride
    );

  const renderComparisonPairs = (group, index, numberOverride) => {
    // The Treatment / Comparator dropdowns list the names typed into Q4 for
    // this same grouping (mirrors the Description of Studies Q8 -> Q9 flow).
    const groupNameOptions = stringSelectOptions([
      ...new Set(
        (group.qExposureTreatmentGroups || [])
          .map(tg => (tg?.name || "").trim())
          .filter(Boolean)
      ),
    ]);
    return renderQuestion(
      "qExposureComparisonPairs",
      <>
        {basePairs(group).map((pair, pairIndex) => (
          <div
            key={`pair-${index}-${pairIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <label>Treatment</label>
              <CFormSelect
                value={pair.treatment}
                options={groupNameOptions}
                onChange={e =>
                  updateGrouping(index, row => ({
                    ...row,
                    qExposureComparisonPairs: basePairs(row).map((p, i) =>
                      i === pairIndex ? { ...p, treatment: e.target.value } : p
                    ),
                  }))
                }
              />
            </div>
            <div className={"form-component"}>
              <label>Comparator</label>
              <CFormSelect
                value={pair.comparator}
                options={groupNameOptions}
                onChange={e =>
                  updateGrouping(index, row => ({
                    ...row,
                    qExposureComparisonPairs: basePairs(row).map((p, i) =>
                      i === pairIndex ? { ...p, comparator: e.target.value } : p
                    ),
                  }))
                }
              />
            </div>
            {pairIndex === 0 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove comparison pair", () =>
                updateGrouping(index, row => ({
                  ...row,
                  qExposureComparisonPairs: basePairs(row).filter(
                    (_, i) => i !== pairIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Comparison Pair", () =>
          updateGrouping(index, row => ({
            ...row,
            qExposureComparisonPairs: [
              ...basePairs(row),
              emptyComparisonPair(),
            ],
          }))
        )}
      </>,
      numberOverride
    );
  };

  const renderDoseGroups = (group, index, numberOverride) =>
    renderQuestion(
      "qExposureDoseGroups",
      <>
        {baseDoseGroups(group).map((dose, doseIndex) => (
          <div
            key={`dose-${index}-${doseIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <CFormInput
                value={dose}
                placeholder={"Dose group label"}
                onChange={e =>
                  updateGrouping(index, row => ({
                    ...row,
                    qExposureDoseGroups: baseDoseGroups(row).map((d, i) =>
                      i === doseIndex ? e.target.value : d
                    ),
                  }))
                }
              />
            </div>
            {baseDoseGroups(group).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove dose group", () =>
                updateGrouping(index, row => ({
                  ...row,
                  qExposureDoseGroups: baseDoseGroups(row).filter(
                    (_, i) => i !== doseIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Dose Group", () =>
          updateGrouping(index, row => ({
            ...row,
            qExposureDoseGroups: [...baseDoseGroups(row), ""],
          }))
        )}
      </>,
      numberOverride
    );

  // A single grouping block (questions 1–5). Answering "No" to Q1 ends the
  // section, so nothing below it is shown.
  const renderGroupingBlock = (group, index) => {
    const comparisonType = group.qExposureComparisonType || "";
    const isFirstBlock = index === 0;
    const blockNumber = index + 1;
    // First block keeps the plain page numbers (1..5); every additional block
    // appends "(blockNumber)" to each question index (e.g. 1(2), 3(2), 4(2)) —
    // the same convention the Description of Studies blocks use.
    const q = n => (isFirstBlock ? n : `${n}(${blockNumber})`);
    // Branch questions are numbered sequentially from 4 in render order, based
    // on the single selected comparison type.
    let branchNumber = 4;
    const nonComparativeGroupNamesNumber =
      comparisonType === "Non-Comparative" ? q(branchNumber++) : null;
    const treatmentGroupsNumber =
      comparisonType === "Comparative" ? q(branchNumber++) : null;
    const comparisonPairsNumber =
      comparisonType === "Comparative" ? q(branchNumber++) : null;
    const doseGroupsNumber =
      comparisonType === "Dose-Finding" ? q(branchNumber++) : null;

    return (
      <>
        <div className={"scs-dos__block-actions"}>
          {duplicateBlockButton(() => duplicateGrouping(index))}
          {index > 0 &&
            removeButton("Remove grouping", () => removeGrouping(index))}
        </div>
        {renderOtherStudies(group, index, q(1))}
        {group.qExposureOtherStudies === true && (
          <>
            {renderTable(group, index, q(2))}
            {renderComparisonType(group, index, q(3))}
            {comparisonType === "Non-Comparative" &&
              renderNonComparativeGroupNames(
                group,
                index,
                nonComparativeGroupNamesNumber
              )}
            {comparisonType === "Comparative" && (
              <>
                {renderTreatmentGroups(group, index, treatmentGroupsNumber)}
                {renderComparisonPairs(group, index, comparisonPairsNumber)}
              </>
            )}
            {comparisonType === "Dose-Finding" &&
              renderDoseGroups(group, index, doseGroupsNumber)}
          </>
        )}
      </>
    );
  };

  const renderGroupings = () => (
    <>
      {/* Expand all / Collapse all, as on the Description of Studies and
          Subject Disposition pages — only worth showing for more than one
          grouping. */}
      {groupings.length > 1 && (
        <div className={"scs-dos__accordion-controls"}>
          <CButton
            color={"primary"}
            shape={"pill"}
            className={"scs-dos__duplicate"}
            onClick={() =>
              setOpenGroupIndexes(new Set(groupings.map((_, i) => i)))
            }>
            Expand all
          </CButton>
          <CButton
            color={"primary"}
            shape={"pill"}
            className={"scs-dos__duplicate"}
            onClick={() => setOpenGroupIndexes(new Set())}>
            Collapse all
          </CButton>
        </div>
      )}
      <div className={"accordion"}>
        {groupings.map((group, index) => {
          const isOpen = openGroupIndexes.has(index);
          const isLast = index === groupings.length - 1;
          return (
            <div
              key={`grouping-${index}`}
              className={"accordion-item"}
              ref={isLast ? lastGroupRef : null}>
              <div className={"accordion-header"}>
                <button
                  type={"button"}
                  ref={el => (accordionButtonRefs.current[index] = el)}
                  className={`accordion-button${isOpen ? "" : " collapsed"}`}
                  onClick={() => toggleGroup(index)}>
                  {group.qGroupingName || `Grouping ${index + 1}`}
                </button>
              </div>
              <CCollapse visible={isOpen} className={"accordion-collapse"}>
                <div className={"accordion-body"}>
                  <div className={"analysis-block"}>
                    {renderGroupingBlock(group, index)}
                  </div>
                </div>
              </CCollapse>
            </div>
          );
        })}
      </div>
      <div className={"scs-dos__block-footer"}>
        {addButton("Add another group", addGrouping)}
        {duplicateAboveButton("Duplicate group above", duplicateGroupingAbove)}
      </div>
    </>
  );

  // Until the grouping names are in we don't know how many accordions to draw,
  // so hold the content back rather than flashing a single empty grouping.
  const isResolvingGroupings =
    (isLoadingGroupings && !groupingsFetched) || !savedLoaded;

  // Reuses the `.scs-dos` styles — the block / accordion / duplicate controls
  // are identical to the Description of Studies wizard.
  const PageContent = () => (
    <div className={"questions-page scs-dos"}>
      <div className={"questions-content"}>
        <div className={"questions-content__items"}>
          <ToastContainer />
          <div className={"questions-content__item"}>
            <div className={"questions-content__label intro-text"}>
              <span className={"questions-content__label-text"}>
                {SCS_OEE_INTRO_TEXT}
              </span>
            </div>
          </div>
          <form id={FORM_ID} onSubmit={handleSubmit}>
            {!isResolvingGroupings && renderGroupings()}
          </form>
          <Tooltip
            id={REMOVE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
          <Tooltip
            id={DUPLICATE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
        </div>
      </div>
    </div>
  );

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Overall Extent of Exposure"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Subject Disposition",
        buttonGoToAction: `/scs/${summaryId}/subject-disposition`,
      }}
      saveButton={true}
      saveButtonHandler={handleSave}
      saveButtonDisabled={isSubmitting}
      nextButton={{
        buttonTitle: isSubmitting ? "Saving..." : "Analysis of Adverse Events",
        buttonFormAction: FORM_ID,
        buttonGoToAction: `/scs/${summaryId}/analysis-of-adverse-events`,
        disabled: isSubmitting,
      }}
    />
  );
};

export default SCSOverallExtentOfExposure;
